import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import dateFormat from 'date-fns/format';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { BillingPaymentPaths } from '@amfam/shared/models';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ValidationService } from '@amfam/ui-kit';

import {
  AccountForSimplifiedPayment,
  OneTimePayActions,
  OneTimePayFeatureFacade,
  OneTimePaySelectors,
  OneTimePaySetup
} from '../../..';
import { ExitType } from '../phoenix-payment-timeout-modal/phoenix-payment-timeout-modal.component';

@Component({
  selector: 'ds-payment-review-wrapper-hs-phoenix',
  templateUrl: './payment-review-wrapper-hs-phoenix.component.html',
  styleUrls: ['./payment-review-wrapper-hs-phoenix.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PaymentReviewWrapperHSPhoenixComponent implements OnInit {
  paymentReviewForm = this.buildForm();
  reviewSubheading: string;
  alertMessage: string;
  primaryButtonName: string;
  secondaryButtonName: string;
  data: OneTimePaySetup;
  amountSubheading: string;
  billAccountOptions$: Observable<AccountForSimplifiedPayment[]>;
  billAccountOptionFiltered: AccountForSimplifiedPayment[];
  regex = /;/g;
  authorizeText: string;
  isHomesite$: Observable<boolean>;
  submitted = false;

  constructor(
    private store: Store,
    private copyService: CopyService,
    private formBuilder: FormBuilder,
    private oneTimePayFacade: OneTimePayFeatureFacade
  ) {
    this.store
      .select(OneTimePaySelectors.selectOneTimePayState)
      .pipe(take(1))
      .subscribe(data => {
        const key = Object.keys(data.entities).pop();
        this.data = data.entities[key];
      });

    this.isHomesite$ = this.store.select(BillAccountsSelectors.selectIsSelectedBillPolicyHomesite);
  }

  ngOnInit(): void {
    this.billAccountOptions$ =
      this.oneTimePayFacade.getSimplifiedPaymentBillAccountsWithAlertMessageList();
    this.billAccountOptions$.subscribe(
      billAccountOptions =>
        (this.billAccountOptionFiltered = billAccountOptions.filter(ba => ba.isSelected))
    );

    this.store
      .select(BrandSelectors.selectECUnderWrittingPartnerData)
      .pipe(take(1))
      .subscribe(data => {
        this.isHomesite$.subscribe(val => {
          if (val) this.authorizeText = this.copyService.getCopy('billing.HSPayment.authorizeText');
          else
            this.authorizeText = this.copyService.getCopy('billing.phoenixPayment.authorizeText', {
              partnerCompanyCopyright: data.ecUWCompanyCopyright,
              partnerCustServPhoneNum: data.ecMainCustServicePhoneNum
            });
        });
      });
    const d: Date = new Date();
    this.reviewSubheading =
      this.copyService.getCopy('billing.payment.reviewSubheading') + dateFormat(d, 'MMM DD, YYYY');
    this.alertMessage = this.copyService.getCopy('billing.payment.alertMessage');
    this.primaryButtonName = this.copyService.getCopy('shared.continueBtn');
    this.secondaryButtonName = this.copyService.getCopy('shared.goBackBtn');
    this.amountSubheading = this.copyService.getCopy('billing.payment.paymentSubheading');
  }

  submit() {
    this.submitted = true;
    if (this.paymentReviewForm.valid) {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [BillingPaymentPaths.SIMPLIFIED_PAYMENT_CONFIRMATION]
        })
      );
    }
  }

  goBackOneStep() {
    this.submitted = false;
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
      })
    );
  }

  handleTimeoutModalExitEvent(exitType: ExitType) {
    if (exitType === 'timeout') {
      this.goBackOneStep();
    } else {
      this.store.dispatch(OneTimePayActions.leaveOTPFlow({}));
    }
  }

  private buildForm() {
    return this.formBuilder.group({
      authorize: [
        null,
        [ValidationService.validateWithKey(Validators.required, 'billing.authorize.required')]
      ]
    });
  }
}
